import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, ShieldCheck, Clock, Star } from 'lucide-react';

const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20">
      {/* Background subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/50 to-white -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-slide-in-left">
            {/* Trust badges */}
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                <ShieldCheck className="w-4 h-4" />
                Professional Service
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                <Clock className="w-4 h-4" />
                Same-Day Service
              </span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Pennsylvania & NJ's{' '}
                <span className="text-primary">Top-Rated</span>{' '}
                Appliance Repair
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
                Expert technicians ready to fix your appliances fast. Same-day or next-day appointments available.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium text-foreground">
                  <span className="font-semibold text-primary">PA (Counties)</span> Philadelphia • Montgomery • Bucks • Delaware
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium text-foreground">
                  <span className="font-semibold text-primary">NJ (Counties)</span> Gloucester • Burlington • Camden
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book">
                <Button
                  data-testid="hero-book-btn"
                  size="lg"
                  className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-white font-semibold px-8 py-6 text-lg rounded-full btn-shine shadow-lg"
                >
                  Book Online Now
                </Button>
              </Link>
              <Button
                data-testid="hero-call-btn"
                size="lg"
                variant="outline"
                className="w-full sm:w-auto font-semibold px-8 py-6 text-lg rounded-full border-2 hover:bg-primary hover:text-white"
                asChild
              >
                <a href="tel:+18556056034">
                  <Phone className="w-5 h-5 mr-2" />
                  (855) 605-6034
                </a>
              </Button>
            </div>

            {/* Trust Stats */}
            <div className="flex flex-wrap gap-8 pt-4">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground font-medium">4.9/5 Rating</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">15+</span> Locations
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-bold text-foreground">10,000+</span> Repairs
              </div>
            </div>

            {/* Service fee notice */}
            <div className="bg-secondary/70 rounded-2xl p-4 border border-border">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Service Call Fee</span> — PA: $89-$129 | NJ: $99-$149 (varies by brand & model)
              </p>
            </div>
          </div>

          {/* Right - BBM Van Image */}
          <div className="relative animate-slide-in-right">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_home-fix-pros-3/artifacts/8lfa84vd_ChatGPT%20Image%20Jan%2023%2C%202026%2C%2009_36_21%20PM.png"
                alt="BBM Appliances Service Van"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
                data-testid="hero-image"
              />
              {/* Clickable phone overlay on van */}
              <a 
                href="tel:+18556056034"
                data-testid="van-phone-link"
                className="absolute top-[45%] left-[28%] bg-transparent hover:bg-primary/10 rounded-lg px-4 py-2 transition-all cursor-pointer"
                style={{ width: '180px', height: '40px' }}
                title="Call (855) 605-6034"
              />
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-border">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="font-heading font-bold text-foreground">Call Center Open</p>
                  <p className="text-sm text-muted-foreground">7AM - 7PM, 7 Days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
